import React, { useEffect, useMemo, useRef } from 'react';
import {
  AlertMessages,
  AuthHandler,
  AuthUIProps,
  getCustomErrorMessage,
  HasuraAuthUI,
  isOIDCError,
  MantineProvider,
  parseLoginErrorCode,
  useRedirectUrlPersistence,
} from '@hasura/auth-ui';
import { notifications } from '@mantine/notifications';
import { useLocation } from 'react-router-dom';

import { authEndpoints, RECAPTCHA_SITE_KEY } from '../../utils/constants';
import { isRedirectToWhiteLabelledConsole } from '../../utils/helpers';
import { authPaths } from '../../utils/routeUtils';
import { loginHandlers } from './login-handlers';
import { getAuthUrl } from './login-handlers/utils/get-auth-url';
import { wait } from './login-handlers/utils/js-utils';

// wrapper that applies the MantineProvider and styles to the AuthUI
type Props = {
  authModes: AuthUIProps['authModes'];
  afterLogin: (data: { loginRedirectLocation: string }) => void;
};

export function HasuraAuthUIWrapper({ afterLogin, authModes }: Props) {
  const hasNotifiedOfConfirmedEmail = useRef(false);
  const location = useLocation();
  const search = location.search;

  const isWhiteLabelledRedirect = isRedirectToWhiteLabelledConsole(location);

  useEffect(() => {
    if (
      new URLSearchParams(search).get('is_confirmed') === 'true' &&
      !hasNotifiedOfConfirmedEmail.current
    ) {
      notifications.show({
        color: 'green',
        message: 'Email confirmed successfully!',
      });
      hasNotifiedOfConfirmedEmail.current = true;
    }
  }, []);

  // OAuth2 failures can remove redirect_url from the search params,
  // the following hook makes sure its persisted for 5 minutes and replace when redirect_url is removed with OAuth2 error
  useRedirectUrlPersistence();

  // Two error-signal flows land on this page:
  //
  //   1. Structured codes (preferred): the auth service redirects OAuth2
  //      failures to /login?error=<code> — see
  //      lux/services/auth/oauth2/error_codes.go for the registry.
  //
  //   2. Legacy OIDC custom-HTML messages: OIDC still uses
  //      /oauth2-error?error_reason=<html>&is_oidc=true so per-org
  //      admin-configured error messages can render. Until OIDC also
  //      migrates to structured codes, we keep the isOIDCError/
  //      getCustomErrorMessage path alongside the new one.
  const errorCode = parseLoginErrorCode(search);
  const isOIDC = isOIDCError(search);
  const oidcCustomMessage = isOIDC ? getCustomErrorMessage(search) : null;

  const alertMessage = useMemo(() => {
    if (errorCode) {
      switch (errorCode) {
        case 'sso_required':
          return authModes.includes('sso')
            ? AlertMessages.OAuthErrorSSOEnabled({
                ssoTo: { pathname: authPaths.sso(), search },
              })
            : AlertMessages.OAuthError();
        case 'session_expired':
          return AlertMessages.SessionExpired();
        case 'state_mismatch':
          return AlertMessages.StateMismatch();
        case 'oauth_cancelled':
          return AlertMessages.OAuthCancelled();
      }
    }
    if (isOIDC) {
      return AlertMessages.OAuthError({ customMessage: oidcCustomMessage });
    }
    return undefined;
  }, [errorCode, isOIDC, oidcCustomMessage, authModes, search]);

  return (
    <MantineProvider
      siblings={
        <>
          <link rel="stylesheet" type="text/css" href="/css/mantine-core.css" />
          <link
            rel="stylesheet"
            type="text/css"
            href="/css/mantine-notifications.css"
          />
          <link
            rel="stylesheet"
            type="text/css"
            href="/css/mantine-custom.css"
          />
        </>
      }
    >
      <HasuraAuthUI
        alertMessage={alertMessage}
        authModes={authModes}
        authPaths={authPaths}
        isWhiteLabelledRedirect={isWhiteLabelledRedirect}
        recaptchaSiteKey={RECAPTCHA_SITE_KEY}
        onGithubSignIn={async () => {
          setTimeout(() => {
            window.location.assign(
              getAuthUrl({
                baseUrl: authEndpoints.githubOAuthUrl,
                search,
              })
            );
          }, 0);

          // suspend the promise for 3 seconds so that the UI stays in the loading state
          // while the user is redirected to the oauth url
          await wait(3000);
        }}
        onGoogleSignIn={async () => {
          setTimeout(() => {
            window.location.assign(
              getAuthUrl({
                baseUrl: authEndpoints.googleOAuthUrl,
                search,
              })
            );
          }, 0);

          // suspend the promise for 3 seconds so that the UI stays in the loading state
          // while the user is redirected to the oauth url
          await wait(3000);
        }}
        onSlackSignIn={async () => {
          setTimeout(() => {
            window.location.assign(
              getAuthUrl({
                baseUrl: authEndpoints.slackOAuthUrl,
                search,
              })
            );
          }, 0);

          // suspend the promise for 3 seconds so that the UI stays in the loading state
          // while the user is redirected to the oauth url
          await wait(3000);
        }}
        onEmailSignIn={props => {
          return loginHandlers
            .signInWithEmail(props)
            .then(data => {
              afterLogin({
                loginRedirectLocation: data.location,
              });
            })
            .catch<AuthHandler>(e => {
              return {
                formError: e.message?.includes('Invalid Credentials')
                  ? {
                      field: 'password',
                      message: 'Invalid email or password',
                    }
                  : undefined,
                notification: {
                  type: 'error',
                  message: e.message ?? 'Something went wrong',
                },
              };
            });
        }}
        onSSOSignIn={props => {
          return loginHandlers
            .signInWithSSO(props)
            .then<void>(async data => {
              setTimeout(() => {
                window.location.replace(data.auth_url);
              }, 0);
              await wait(500);
            })
            .catch<AuthHandler>(e => {
              return {
                notification: {
                  type: 'error',
                  message: e.message ?? 'Something went wrong',
                },
              };
            });
        }}
        onForgotPassword={props => {
          return loginHandlers
            .resetPassword(props)
            .then<AuthHandler>(data => {
              return {
                notification: {
                  type: 'success',
                  message: data.message,
                },
                search: data.search,
              };
            })
            .catch<AuthHandler>(e => {
              return {
                notification: {
                  type: 'error',
                  message: e.message ?? 'Something went wrong',
                },
              };
            });
        }}
        onRegister={props => {
          return loginHandlers
            .registerNewUser(props)
            .then<AuthHandler>(data => {
              return {
                notification: {
                  type: 'success',
                  message: data.message,
                },
              };
            })
            .catch<AuthHandler>(e => {
              return {
                notification: {
                  type: 'error',
                  message: e.message ?? 'Something went wrong',
                },
              };
            });
        }}
      />
    </MantineProvider>
  );
}
